//==================================================================================================
// Each tile can expand to show container-level breakdowns and a totals/markup grid.
// The component also lets you "Save" a given offer by POSTing the original raw payload.
// Now uses ResultsTile for each row so the same UI can be reused in a single-offer screen.
// ===================================================================================================

import React, { useMemo, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import { LoadingContext, BufferService } from "src/M_Components";
import ResultsTile from "./ResultsTile";

// Shared responsive styles for panel + tiles
import "./ResultsPanel.css";

//==========================================
// Main results panel for offers; handles list and save action
//==================================
export default function ResultsPanel({
  visible,
  onClose,
  results,
  t,
  toastMessages,
}) {
  const ctx = useContext(LoadingContext); // global loading and base URL live here

  // svcSave will POST to RateOffers using the base API URL coming from context.
  // useMemo keeps the instance stable as long as DEFAULTURL does not change.
  const svcSave = useMemo(
    () => new BufferService("RateOffers", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  //==========================================
  // POSTs the original offer payload (__raw if present) to the RateOffers endpoint.
  // Shows success/error toasts and wraps the call in the global loading spinner.
  //==================================
  const onSave = useCallback(
    async (offer) => {
      try {
        ctx.setIsLoad(true); // show global loading spinner
        await svcSave.post(offer?.__raw || offer || {}); // prefer the raw copy when present
        toastMessages?.(
          "success",
          "",
          t("SavedSuccessfully") || "Saved successfully."
        );
      } catch (err) {
        toastMessages?.(
          "error",
          "",
          err?.message || t("ErrorSaving") || "Error saving offer."
        );
      } finally {
        ctx.setIsLoad(false); // always turn spinner off
      }
    },
    [ctx, svcSave, toastMessages, t]
  );

  return (
    <div
      className={`results-panel-root ${
        visible ? "" : "results-panel-root--hidden"
      }`}
    >
      {/* Header (spec) - bold title with a subtle bottom border to visually anchor the section */}
      <div className="results-panel-header">{t("Search Results")}</div>

      {/* Empty list message */}
      {(results || []).length === 0 ? (
        <div className="results-panel-empty">{t("No results")}</div>
      ) : (
        <div className="results-panel-list">
          {(results || []).map((r, idx) => (
            // delegate the full rendering to the reusable tile
            <ResultsTile
              key={idx}
              index={idx}
              offer={r}
              t={t}
              onSave={onSave}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// PropTypes keep the contract tight so the component is self-documenting in dev tools
ResultsPanel.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  results: PropTypes.array,
  t: PropTypes.func,
  toastMessages: PropTypes.func,
};
